import React from "react";
import FAQ from "../../pages/Faq";

import { Helmet } from "react-helmet-async";
import {
  FaBriefcase,
  FaEnvelopeOpenText,
  FaEnvelope,
  FaCaretRight,
  FaClock,
  FaListCheck,
  FaThumbsUp,
  FaBuilding,
  FaBell,
  FaFileLines,
  FaCircleInfo,
} from "react-icons/fa6";

import {
  FiClock,
  FiFileText,
  FiAlertTriangle,
  FiMessageCircle,
  FiUsers,
  FiBriefcase,
  FiUserCheck,
} from "react-icons/fi";
import { Check } from "lucide-react";
import SalaryRevisionLetterGeneratorForm from "./SalaryRevisionLetterGeneratorForm";
import ConfirmationLetterGeneratorForm from "./ConfirmationLetterGeneratorForm";

const steps = [
  {
    number: 1,
    title: "Enter Company Details",
    description: "Add the company name, address, and contact information",
  },
  {
    number: 2,
    title: "Add Employee Information",
    description: "Enter the employee's name, ID, designation, and department",
  },
  {
    number: 3,
    title: "Provide Employment Dates",
    description:
      "Add the joining date, resignation date, last working date, and relieving date",
  },
  {
    number: 4,
    title: "Confirm Notice Period Status",
    description:
      "Select whether the notice period was fully served, partially served, waived, or adjusted",
  },
  {
    number: 5,
    title: "Confirm Exit Formalities",
    description:
      "Enter the handover, asset return, and departmental clearance status",
  },
  {
    number: 6,
    title: "Add Settlement Details",
    description:
      "Mention whether the full and final settlement is complete or will be processed separately",
  },
  {
    number: 7,
    title: "Generate the Letter",
    description:
      "The system will insert the information into the standard relieving letter template",
  },
  {
    number: 8,
    title: "Review and Issue",
    description:
      "Verify every date and exit detail before signing or sharing the document",
  },
];

const features = [
  <>
    <strong>Startups:</strong> Prepare relieving letters quickly when employees
    complete their exit process.
  </>,
  <>
    <strong>SMEs:</strong> Maintain a consistent format for employee separation
    documentation.
  </>,
  <>
    <strong>HR Teams:</strong> Create accurate relieving letters across
    different roles and departments.
  </>,
  <>
    <strong>Large Organisations:</strong> Standardise relieving documentation
    across multiple teams and locations.
  </>,
  <>
    <strong>Recruitment Agencies:</strong> Provide formal release documents for
    contract and temporary employees.
  </>,
  <>
    <strong>Professional Firms:</strong> Document employee exits clearly as part
    of the final separation process.
  </>,
];

const Faqs1 = [
  {
    title: "Can I Create a Relieving Letter Online for Free?",
    content:
      "Yes. You can create a relieving letter online by entering the required employee and employment details into the generator. Once completed, the relieving letter can be generated and downloaded as a professional PDF.",
  },
  {
    title: "Can I Download the Relieving Letter as a PDF?",
    content:
      "Yes. After entering the required information, the generator creates a formatted relieving letter that you can download as a PDF, making it ready to save, print, or share with the employee.",
  },
  {
    title: "What Information Do I Need to Create a Relieving Letter?",
    content:
      "You typically need the employee’s name, job title, department, joining date, last working date, resignation date, notice period status, company details, and other relevant separation information required for the relieving letter.",
  },
  {
    title: "Can I Create a Relieving Letter Without Using a Template?",
    content:
      "Yes. Instead of manually editing a relieving letter template, you can enter the required details into the online generator. The tool organizes the information into a professional document automatically.",
  },
  {
    title: "Can HR Teams Use an Online Relieving Letter Generator?",
    content:
      "Yes. HR professionals, business owners, and administrators can use it to create relieving letters for employees without repeatedly formatting documents. It is particularly useful when multiple employee separation documents need to be prepared.",
  },
  {
    title: "How Quickly Can I Generate a Relieving Letter?",
    content:
      "You can create a relieving letter in minutes by entering the required employment and exit details. Once the information is complete, generate the document and download the finished relieving letter as a PDF.",
  },
];

const schema = [
  // =========================================================
  // SCHEMA 1: WebApplication
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Waggex Free Relieving Letter Generator",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "Free online relieving letter generator. Enter company details, employee information, last working date, notice period, and clearance details to create and download a professional relieving letter PDF instantly.",
    url: "https://www.waggex.com/hr-documents/relieving-letter-generator",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    featureList: [
      "Free relieving letter generator India",
      "Online relieving letter format India",
      "Relieving letter PDF download",
      "Employee relieving letter template India",
      "Professional relieving letter maker India",
    ],
  },

  // =========================================================
  // SCHEMA 2: FAQPage
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is a relieving letter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A Relieving Letter is an official document confirming that an employee has completed the required exit process and has been formally released from their duties, including joining date, last working date, relieving date, and exit formality confirmation.",
        },
      },
      {
        "@type": "Question",
        name: "When should a relieving letter be issued?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A relieving letter should be issued on the employee's last working day or within a few working days after departure, ideally alongside the experience letter after full and final settlement is completed.",
        },
      },
      {
        "@type": "Question",
        name: "Is a relieving letter different from an experience letter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. A relieving letter confirms the employee has been released from duties and exit formalities are complete. An experience letter confirms designation, tenure, and work performance. Both are commonly issued together at exit.",
        },
      },
      {
        "@type": "Question",
        name: "Can both the relieving letter and experience letter be combined?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Some organisations issue a single combined document covering both relieving and experience letter content. Others issue them separately based on HR policy. Waggex offers a separate generator for each document type.",
        },
      },
    ],
  },

  // =========================================================
  // SCHEMA 3: Organization
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",

    // Logo URL kept unchanged as requested
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",

    description:
      "Waggex provides free HR document generation tools and HRMS software for Indian businesses.",
    areaServed: "IN",
  },
];

const relievingLetterContent = [
  {
    icon: FaBuilding,
    title: "Company and Employee Details",
    description:
      "The organization's name, employee name, Address, designation & department.",
  },
  {
    icon: FaClock,
    title: "Employment Duration",
    description: "The employee's date of joining and last working date.",
  },
  {
    icon: FaListCheck,
    title: "Resignation Acknowledgement",
    description:
      "A formal statement acknowledging the employee's resignation or approved separation.",
  },
  {
    icon: FaBriefcase,
    title: "Release From Duties",
    description:
      "Confirmation that the employee has been relieved from their duties from the effective relieving date.",
  },
  {
    icon: FaBell,
    title: "Notice Period Status",
    description:
      "Information confirming whether the notice period was served, waived, or adjusted.",
  },
  {
    icon: FaFileLines,
    title: "Handover and Clearance",
    description:
      "An optional statement confirming work handover, asset return, and departmental clearance.",
  },
  {
    icon: FaCircleInfo,
    title: "Settlement Information",
    description:
      "An optional statement confirming whether the full and final settlement has been completed or will be processed separately.",
  },
  {
    icon: FaThumbsUp,
    title: "Appreciation Statement",
    description:
      "A professional closing message thanking the employee and wishing them success.",
  },
];

const relievingLetterPoints = [
  "The required notice period",
  "Work handover",
  "Company asset return",
  "Departmental clearance",
  "Exit formalities",
];

const companystepsFeatures = [
  {
    id: 1,
    icon: <FiClock />,
    title: "Save HR Time",
    desc: "Generate a relieving letter without drafting every exit document manually",
  },
  {
    id: 2,
    icon: <FiFileText />,
    title: "Maintain a Standard Format",
    desc: "Use a consistent relieving letter format for employees across departments and locations",
  },
  {
    id: 3,
    icon: <FiAlertTriangle />,
    title: "Reduce Documentation Errors",
    desc: "Structured form fields help prevent incorrect employee IDs, dates, and separation details",
  },
  {
    id: 4,
    icon: <FiUsers />,
    title: "Improve Employee Offboarding",
    desc: "Include the Employee Relieving Letter as a standard part of the exit process",
  },
  {
    id: 5,
    icon: <FiUserCheck />,
    title: "Provide Clear Employment Closure",
    desc: "Formally confirm that the employee has been released from their duties",
  },
  {
    id: 6,
    icon: <FiFileText />,
    title: "Maintain Professional Records",
    desc: "Store the issued letter in the employee's official HR file",
  },
];
const experiencePoints = [
  "Employment duration",
  "Designation",
  "Department",
  "Responsibilities",
  "Work experience",
  "Performance or conduct",
];

const relievingPoints = [
  "Completion of exit formalities",
  "Last working date",
  "Notice period status",
  "Effective relieving date",
  "Release from duties",
];
export default function ConfirmationLetterGenerator() {
  return (
    <>
      <Helmet>
        <title>Free Professional Confirmation Letter Online | PDF Format</title>
        <meta
          name="description"
          content="Create a professional confirmation letter online with Waggex. Add employee details, last working date & clearance to download instantly"
        />
        <meta
          name="keywords"
          content="confirmation letter generator online,confirmation letter format India,free confirmation letter maker online India,confirmation letter template India,confirmation letter PDF generator India,employee confirmation letter format India,confirmation letter Word format India,confirmation letter sample India,job confirmation letter online India"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/hr-documents/confirmation-letter-generator"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <div className="px-2 py-3 sm:px-4">
        <section className="rounded-xl border border-white px-2 sm:px-5 py-7 shadow-sm backdrop-blur bg-[#ffffff40]">
          <div className="text-center mb-10">
            <h1 className="text-[#10324F] text-lg sm:text-3xl font-roboto font-bold leading-snug mb-4">
              Create a Professional Confirmation Letter
            </h1>

            <p className="mx-auto mt-8 max-w-5xl text-sm sm:text-lg leading-6 text-[#10324F] min-h-18 sm:min-h-12">
              Generate a clear and professionally structured Confirmation Letter
              after an employee successfully completes their probation period
            </p>

            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row mb-10">
              <button
                onClick={() =>
                  (window.location.href = "https://app.waggex.com/signup")
                }
                className="w-full sm:w-auto bg-[#10324F] hover:bg-[#10324F] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
              >
                Start Free Trial
              </button>

              <button
                onClick={() => (window.location.href = "/contact-us")}
                className="w-full sm:w-auto border border-[#10324F] text-[#10324F] hover:bg-[#10324F] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
              >
                Book a Demo
              </button>
            </div>
          </div>
          <ConfirmationLetterGeneratorForm />
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 ">
                {/* Left Content */}
                <div>
                  <div className="mt-4">
                    <div className="mb-6 flex items-center gap-3">
                      <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                      <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                        WHAT IS A RELIEVING LETTER?
                      </span>
                    </div>

                    <h2 className="text-xl md:text-3xl font-semibold text-[#10324F] mt-3 mb-5">
                      Its an Formal Documentation of an Employee’s Release
                    </h2>

                    <p className="text-[#10324F] mb-6 leading-relaxed max-w-2xl">
                      A relieving letter confirms that an employee has formally
                      completed the exit process and been released from their
                      duties. It records key employment details such as
                      designation, joining date, last working day, and official
                      relieving date
                    </p>
                  </div>
                </div>

                {/* Right Content */}
                <div className="flex flex-col">
                  {relievingLetterPoints.map((item, index) => (
                    <React.Fragment key={item}>
                      <div className="relative z-10 flex min-h-12.5 items-center justify-center rounded-lg border border-white  bg-linear-to-b from-white to-[#D1DFEA]/80  hover:bg-white/90 px-4 py-3 text-center text-sm font-medium text-[#10324F] shadow-sm backdrop-blur-sm transition sm:min-h-12.5 sm:text-base">
                        {item}
                      </div>

                      {index < relievingLetterPoints.length - 1 && (
                        <div className="mx-auto h-6 border-l border-dashed border-[#10324F]/80" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  WHAT DOES THE RELIEVING LETTER TEMPLATE INCLUDE?
                </p>
                <h2 className="mt-4 text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F] uppercase">
                  Create A Professional Relieving Letter PDF
                </h2>

                <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
                  Add key separation information such as the employee’s last
                  working date and relieving date, along with company and
                  employment details. The tool organizes everything into a
                  formal relieving letter ready to download
                </p>
              </div>

              {/* Cards */}
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                {relievingLetterContent.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={index}
                      className="group flex min-h-25.5 items-start gap-4 rounded-xl border border-white/90 bg-linear-to-b from-white/60 to-[#D1DFEA]/70  hover:bg-white/90 px-5 py-5 transition-all duration-200 shadow-sm"
                    >
                      {/* Icon */}
                      <div className="flex shrink-0 items-center justify-center pt-1">
                        <Icon className="text-2xl text-[#10324F]" />
                      </div>

                      {/* Content */}
                      <div>
                        <h3 className="text-sm sm:text-lg font-semibold leading-5 text-[#10324F]">
                          {item.title}
                        </h3>

                        <p className="mt-2 text-xs sm:text-sm leading-5 text-[#10324F]">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  HOW TO CREATE A RELIEVING LETTER ONLINE
                </p>

                <h2 className="mt-4 text-xl font-bold leading-tight text-[#10324F] sm:text-3xl">
                  Create Your Relieving Letter in 3 Simple Steps
                </h2>
              </div>

              {/* Steps */}

              {/* ROW 1 */}
              <div className=" mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(0, 4).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE */}
                    <div className="absolute left-0 right-1/2 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 3 && (
                      <div className="absolute left-1/2 right-0 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#10324F] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#1324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ROW 2 */}
              <div className=" mt-4 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(4, 8).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE — IMPORTANT FOR STEP 5 */}
                    <div className="absolute left-0 right-1/2 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 3 && (
                      <div className="absolute left-1/2 right-0 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#163957] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#10324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-8 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-8">
                {/* LEFT CONTENT */}
                <div>
                  {/* Small heading */}

                  <div className="mb-6 flex items-center gap-3">
                    <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                    <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                      UNDERSTANDING THE DIFFERENCE
                    </span>
                  </div>

                  {/* Main heading */}
                  <h2 className="text-xl font-semibold leading-tight text-[#10324F] sm:text-3xl">
                    Relieving Letter vs Experience Letter: What’s the
                    Difference?
                  </h2>

                  {/* Intro */}
                  <p className="mt-7 max-w-2xl text-sm leading-8 text-[#10324F]">
                    Both documents are commonly provided when an employee leaves
                    a company, but they confirm different things. A relieving
                    letter confirms formal release from employment, while an
                    experience letter records the employee’s work history.
                    <br /> <strong>CAN BOTH BE COMBINED? </strong> <br />
                    Yes. Employers may issue a single document covering both
                    employment experience and formal relieving details,
                    depending on their HR process.
                  </p>
                </div>

                {/* RIGHT COMPARISON */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* ================= OFFER LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelopeOpenText className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Relieving Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-73 rounded-xl border border-white bg-linear-to-b  from-white/60 to-[#D1DFEA]/30 px-4 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {relievingPoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ================= APPOINTMENT LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelope className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Experience Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-73 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {experiencePoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  Why Use an Online Relieving Letter Format?
                </p>
                <h2 className="text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F]  uppercase">
                  Create Professional Exit Documents Faster
                </h2>
              </div>
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-4">
                {companystepsFeatures.map((item) => {
                  // const Icon = item.icon;

                  return (
                    <div
                      key={item.id}
                      className="rounded-xl border border-white/90  bg-linear-to-b from-white/60 to-[#D1DFEA]/60  hover:bg-white/30 p-4 duration-200 shadow-sm transition-all"
                    >
                      {/* Top Row */}
                      <div className="flex items-center justify-center mb-5">
                        {/* <div className="w-14 h-10 rounded-md bg-[#16324F] font-roboto text-white flex items-center justify-center text-lg font-bold shadow">
                          {item.id}
                        </div> */}
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10324F] text-xl text-white">
                          {item.icon}
                        </div>
                      </div>

                      {/* Content */}
                      <h2 className="text-center text-sm sm:text-base font-semibold text-[#10324F] font-roboto">
                        {item.title}
                      </h2>

                      <p className="mt-2 text-center text-sm leading-6 text-[#10324F] font-roboto">
                        {item.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto mb-4">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  WHO CAN USE THIS RELIEVING LETTER FORMAT?
                </p>
                <h2 className="font-roboto text-center text-[#10324F] text-xl md:text-3xl font-bold uppercase tracking-wide">
                  Suitable for Different Employers
                </h2>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 place-items-start">
                {features.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 text-[#10324F] px-2 py-3  rounded-2xl"
                  >
                    {/* Check Icon */}
                    <div className="mt-1 flex items-center justify-center text-[#10324F]">
                      <Check size={30} aria-hidden="true" />
                    </div>

                    {/* Text */}
                    <p className="font-roboto text-sm  font-medium leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <FAQ
            title="Frequently Asked Question"
            items={Faqs1}
            defaultOpenIndex={0}
          />
          <section className="w-full bg-[#F4F6F8]/60 px-6 py-10 rounded-xl border border-white mt-8">
            <div className="max-w-6xl mx-auto text-center mt-4 mb-4">
              <h2 className="text-[#10324F] text-xl sm:text-3xl font-roboto font-bold uppercase">
                Create Relieving Letter Document In Minutes
              </h2>

              <p className="mt-4 text-[#10324F] text-sm sm:text-base">
                Create your relieving letter in minutes with no login, no
                watermark, and no waiting. Just enter the employee details,
                generate the letter, and download your PDF instantly
              </p>

              <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3
              rounded-xl
              bg-[#10324F]
              text-white text-xs sm:text-sm
              font-medium
              shadow-md
              transition-all duration-300
              hover:scale-105
            "
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                >
                  Generate Relieving Letter
                </button>

                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3 text-xs sm:text-sm
              rounded-xl
              border border-[#10324F]
              text-[#10324F]
              font-medium
              transition-all duration-300
              hover:bg-white
              hover:text-[#16385C]
            "
                  onClick={() => (window.location.href = "/all-hr-documents")}
                >
                  Explore more
                </button>
              </div>
            </div>
          </section>
        </section>
      </div>
    </>
  );
}
